import { useRef, useState } from "react";

import { Button } from "../../ui/button";
import { SelectField, TextAreaField } from "../../ui/fields";
import { Notice } from "../../ui/notice";
import type { AnyRequestCommand, RequestCommandOutcome, RequestCommandResult } from "../commands";
import type { RequestFacts } from "../detail-model";
import { describeRequestError, type RequestFailure } from "../errors";
import { type BuilderRow, type RowErrors, validateRows } from "../field-builder";
import {
  completionEvidenceOptions,
  noteKindOptions,
  priorityOptions,
  workStatusLabels,
  workStatusOptions,
} from "../labels";
import { useRequestAssignees } from "../queries";
import { RequestCommandDialog } from "./CommandDialog";
import { FieldBuilder } from "./FieldBuilder";
import { hintClass } from "./parts";

// One dialog for each staff command of the request lifecycle. Each takes the `expectedVersion` from the facts the page last read
// (`facts.version`), so a stale screen sends a stale version and gets a visible 409. The shell (`CommandDialog.tsx`) adds the internal
// reason and the failure rules.

type Common = Readonly<{
  facts: RequestFacts;
  run: (command: AnyRequestCommand) => Promise<RequestCommandResult>;
  onDone: (outcome: RequestCommandOutcome) => void;
  onClose: () => void;
}>;

const STATUS_LINE_MAX = 500;

// --- claim -------------------------------------------------------------------------------------------------------------------------

export function ClaimDialog({ facts, run, onDone, onClose }: Common) {
  return (
    <RequestCommandDialog
      title="Accept this request?"
      description="You become the owner and start the work. The request moves to In progress."
      confirmLabel="Accept and start"
      run={run}
      onDone={onDone}
      onClose={onClose}
      build={() => ({
        ok: true,
        command: (reason) => ({
          command: "claim",
          requestId: facts.id,
          body: { expectedVersion: facts.version, reason },
        }),
      })}
    />
  );
}

// --- complete ----------------------------------------------------------------------------------------------------------------------

export function CompleteDialog({ facts, run, onDone, onClose }: Common) {
  const [kind, setKind] = useState("");
  const [error, setError] = useState<string | undefined>();
  return (
    <RequestCommandDialog
      title="Complete this request?"
      description="The request is closed as completed. Choose what shows that the work was done."
      confirmLabel="Complete request"
      run={run}
      onDone={onDone}
      onClose={onClose}
      build={() => {
        if (kind === "") {
          setError("Choose the evidence of completion.");
          return { ok: false };
        }
        setError(undefined);
        return {
          ok: true,
          command: (reason) => ({
            command: "complete",
            requestId: facts.id,
            body: {
              expectedVersion: facts.version,
              reason,
              evidenceKind: kind as "SERVICE_DELIVERED",
            },
          }),
        };
      }}
    >
      <SelectField
        id="request-complete-evidence"
        label="Evidence of completion"
        value={kind}
        onChange={setKind}
        options={completionEvidenceOptions}
        placeholder="Choose the evidence"
        required
        error={error}
      />
    </RequestCommandDialog>
  );
}

// --- unavailable, decline, cancel --------------------------------------------------------------------------------------------------

const CLOSURE_WORDING = {
  unavailable: {
    title: "Mark this request as unavailable?",
    description:
      "Use this when the supplier cannot provide it. The request is closed as unavailable. Customer-safe alternatives go in a customer update, not here.",
    confirm: "Mark unavailable",
  },
  decline: {
    title: "Decline this request?",
    description: "Lafayette will not take this request. The request is closed as declined.",
    confirm: "Decline request",
  },
  cancel: {
    title: "Cancel this request?",
    description: "The request is closed as cancelled. This cannot be undone.",
    confirm: "Cancel request",
  },
} as const;

export function ClosureDialog({
  kind,
  facts,
  run,
  onDone,
  onClose,
}: Common & Readonly<{ kind: "unavailable" | "decline" | "cancel" }>) {
  const wording = CLOSURE_WORDING[kind];
  const [line, setLine] = useState("");
  const [error, setError] = useState<string | undefined>();
  return (
    <RequestCommandDialog
      title={wording.title}
      description={wording.description}
      confirmLabel={wording.confirm}
      tone="danger"
      run={run}
      onDone={onDone}
      onClose={onClose}
      build={() => {
        if (line.trim().length > STATUS_LINE_MAX) {
          setError(`Use at most ${STATUS_LINE_MAX} characters.`);
          return { ok: false };
        }
        setError(undefined);
        const summary = line.trim();
        return {
          ok: true,
          command: (reason) => ({
            command: kind,
            requestId: facts.id,
            body: {
              expectedVersion: facts.version,
              reason,
              ...(summary === "" ? {} : { customerStatusSummary: summary }),
            },
          }),
        };
      }}
    >
      <TextAreaField
        id="request-customer-line"
        label="Line the customer sees (optional)"
        hint="Shown to the customer as the status of their request. Leave it empty to use the standard words. Do not write the internal reason, a supplier name or a contact here."
        value={line}
        rows={3}
        maxLength={STATUS_LINE_MAX}
        error={error}
        onChange={setLine}
      />
    </RequestCommandDialog>
  );
}

// --- priority, work status, status line --------------------------------------------------------------------------------------------

export function PriorityDialog({ facts, run, onDone, onClose }: Common) {
  const [priority, setPriority] = useState<string>(facts.priority);
  const [error, setError] = useState<string | undefined>();
  return (
    <RequestCommandDialog
      title="Change the priority"
      description="The priority is staff only. The customer does not see it."
      confirmLabel="Change priority"
      run={run}
      onDone={onDone}
      onClose={onClose}
      build={() => {
        if (priority === facts.priority) {
          setError("Choose a different priority.");
          return { ok: false };
        }
        setError(undefined);
        return {
          ok: true,
          command: (reason) => ({
            command: "priority",
            requestId: facts.id,
            body: {
              expectedVersion: facts.version,
              reason,
              priority: priority as "STANDARD" | "HIGH" | "URGENT",
            },
          }),
        };
      }}
    >
      <SelectField
        id="request-priority"
        label="Priority"
        value={priority}
        onChange={setPriority}
        options={priorityOptions}
        required
        error={error}
      />
    </RequestCommandDialog>
  );
}

export function WorkStatusDialog({ facts, run, onDone, onClose }: Common) {
  const [status, setStatus] = useState<string>(facts.workStatus);
  const [error, setError] = useState<string | undefined>();
  return (
    <RequestCommandDialog
      title="Set where the work waits"
      description={`Now: ${workStatusLabels[facts.workStatus]}. This marker is staff only and does not change the state of the request.`}
      confirmLabel="Set work status"
      run={run}
      onDone={onDone}
      onClose={onClose}
      build={() => {
        if (status === facts.workStatus) {
          setError("Choose a different status.");
          return { ok: false };
        }
        setError(undefined);
        return {
          ok: true,
          command: (reason) => ({
            command: "workStatus",
            requestId: facts.id,
            body: {
              expectedVersion: facts.version,
              reason,
              workStatus: status as "NONE" | "WITH_SUPPLIER" | "INTERNAL_REVIEW",
            },
          }),
        };
      }}
    >
      <SelectField
        id="request-work-status"
        label="Work status"
        value={status}
        onChange={setStatus}
        options={workStatusOptions}
        required
        error={error}
      />
    </RequestCommandDialog>
  );
}

export function StatusSummaryDialog({ facts, run, onDone, onClose }: Common) {
  const [summary, setSummary] = useState(facts.customerStatusSummary ?? "");
  const [error, setError] = useState<string | undefined>();
  return (
    <RequestCommandDialog
      title="Change the line the customer sees"
      description="This line is shown to the customer as the status of their request. Write only what the customer may read."
      confirmLabel="Save line"
      run={run}
      onDone={onDone}
      onClose={onClose}
      build={() => {
        const trimmed = summary.trim();
        if (trimmed === "" || trimmed.length > STATUS_LINE_MAX) {
          setError(
            trimmed === "" ? "Write the line." : `Use at most ${STATUS_LINE_MAX} characters.`,
          );
          return { ok: false };
        }
        setError(undefined);
        return {
          ok: true,
          command: (reason) => ({
            command: "statusSummary",
            requestId: facts.id,
            body: { expectedVersion: facts.version, reason, summary: trimmed },
          }),
        };
      }}
    >
      <TextAreaField
        id="request-status-line"
        label="Line the customer sees"
        value={summary}
        rows={3}
        maxLength={STATUS_LINE_MAX}
        required
        error={error}
        onChange={setSummary}
      />
    </RequestCommandDialog>
  );
}

// --- reassign ----------------------------------------------------------------------------------------------------------------------

const roleWords: Readonly<Record<"concierge" | "supervisor", string>> = {
  concierge: "concierge",
  supervisor: "supervisor",
};

/** What a refused reassign means for the person who pressed the button. The server's refusal is the authority; this only explains it. */
function reassignHint(failure: RequestFailure): string | null {
  if (failure.kind === "forbidden") {
    return "A supervisor can give a request to any active concierge or supervisor. The owner can hand it only to a colleague who has an active assignment to this customer.";
  }
  if (failure.kind === "conflict" && failure.conflictCode === "request.assignee_ineligible") {
    return "Close this window and choose a colleague who has an active assignment to the customer.";
  }
  return null;
}

export function ReassignDialog({
  facts,
  holderStaffUserId,
  viewerStaffUserId,
  assigning,
  run,
  onDone,
  onClose,
}: Common &
  Readonly<{
    /** The staff member who holds the request now (the owner, or the designated assignee while ASSIGNED). They are not offered. */
    holderStaffUserId: string | null;
    /** The signed-in staff member, when known: the list marks them "(you)". */
    viewerStaffUserId: string | null;
    /** True for a request nobody holds: the words say "assign". */
    assigning: boolean;
  }>) {
  const directory = useRequestAssignees(true);
  const [choice, setChoice] = useState("");
  const [error, setError] = useState<string | undefined>();
  const people = (directory.data?.assignees ?? []).filter(
    (person) => person.staffUserId !== holderStaffUserId,
  );
  const options = people.map((person) => ({
    value: person.staffUserId,
    label: `${person.displayName}${person.staffUserId === viewerStaffUserId ? " (you)" : ""} — ${person.roles
      .map((role) => roleWords[role])
      .join(" and ")}`,
  }));
  const listFailure = directory.isError ? describeRequestError(directory.error) : null;
  return (
    <RequestCommandDialog
      title={assigning ? "Assign this request" : "Reassign this request"}
      description={
        assigning
          ? "Nobody holds this request. The person you choose becomes its holder."
          : "The person you choose becomes the new holder. They need an active assignment to this customer unless you are a supervisor."
      }
      confirmLabel={assigning ? "Assign request" : "Reassign request"}
      run={run}
      onDone={onDone}
      onClose={onClose}
      failureHint={reassignHint}
      build={() => {
        if (choice === "" || !people.some((person) => person.staffUserId === choice)) {
          setError("Choose who takes the request.");
          return { ok: false };
        }
        setError(undefined);
        return {
          ok: true,
          command: (reason) => ({
            command: "reassign",
            requestId: facts.id,
            body: { expectedVersion: facts.version, reason, assigneeStaffUserId: choice },
          }),
        };
      }}
    >
      {directory.isPending ? (
        <p className={hintClass} role="status">
          Loading the staff list…
        </p>
      ) : listFailure !== null ? (
        <Notice tone="danger" role="alert">
          <p>
            {listFailure.kind === "forbidden"
              ? "You may not see the list of staff. Ask a supervisor to assign this request."
              : `The staff list did not load. ${listFailure.message}`}
          </p>
          {listFailure.kind === "forbidden" ? null : (
            <Button variant="outline" size="sm" onClick={() => void directory.refetch()}>
              Load the list again
            </Button>
          )}
        </Notice>
      ) : (
        <SelectField
          id="request-assignee"
          label="New holder"
          value={choice}
          onChange={setChoice}
          options={options}
          placeholder={
            options.length === 0 ? "Nobody else can take this request" : "Choose a person"
          }
          required
          error={error}
          disabled={options.length === 0}
          hint="The list holds the active concierges and supervisors. The server checks that the person may take this request."
        />
      )}
    </RequestCommandDialog>
  );
}

// --- ask the customer --------------------------------------------------------------------------------------------------------------

const QUESTION_MAX = 1000;

export function AskCustomerDialog({ facts, run, onDone, onClose }: Common) {
  const [question, setQuestion] = useState("");
  const [rows, setRows] = useState<readonly BuilderRow[]>([]);
  const [questionError, setQuestionError] = useState<string | undefined>();
  const [rowErrors, setRowErrors] = useState<readonly RowErrors[] | undefined>();
  const counter = useRef(0);
  const nextRowId = () => {
    counter.current += 1;
    return counter.current;
  };
  return (
    <RequestCommandDialog
      title="Ask the customer for information"
      description="The customer sees one Needs You action with your question. The request moves to Waiting for the customer until they answer."
      confirmLabel="Ask the customer"
      run={run}
      onDone={onDone}
      onClose={onClose}
      build={() => {
        const prompt = question.trim();
        const promptProblem =
          prompt === ""
            ? "Write the question."
            : prompt.length > QUESTION_MAX
              ? `Use at most ${QUESTION_MAX} characters.`
              : undefined;
        setQuestionError(promptProblem);
        const checked = validateRows(rows);
        setRowErrors(checked.ok ? undefined : checked.errors);
        if (promptProblem !== undefined || !checked.ok) return { ok: false };
        return {
          ok: true,
          command: (reason) => ({
            command: "askCustomer",
            requestId: facts.id,
            body: {
              expectedVersion: facts.version,
              reason,
              question: prompt,
              ...(checked.fields.length === 0 ? {} : { fields: checked.fields }),
            },
          }),
        };
      }}
    >
      <TextAreaField
        id="request-question"
        label="Question for the customer"
        hint="The customer reads this text. Do not include supplier names, prices or internal notes."
        value={question}
        rows={3}
        maxLength={QUESTION_MAX}
        required
        error={questionError}
        onChange={setQuestion}
      />
      <FieldBuilder
        rows={rows}
        onChange={setRows}
        errors={rowErrors}
        idPrefix="request-field"
        nextRowId={nextRowId}
      />
    </RequestCommandDialog>
  );
}

// --- internal note -----------------------------------------------------------------------------------------------------------------

const NOTE_MAX = 4000;

export function NoteDialog({ facts, run, onDone, onClose }: Common) {
  const [kind, setKind] = useState("STAFF_NOTE");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | undefined>();
  return (
    <RequestCommandDialog
      title="Add an internal note"
      description="Notes are staff only. They are append-only: a note cannot be edited or deleted."
      confirmLabel="Add note"
      needsReason={false}
      run={run}
      onDone={onDone}
      onClose={onClose}
      build={() => {
        const text = body.trim();
        if (text === "" || text.length > NOTE_MAX) {
          setError(text === "" ? "Write the note." : `Use at most ${NOTE_MAX} characters.`);
          return { ok: false };
        }
        setError(undefined);
        return {
          ok: true,
          command: () => ({
            command: "note",
            requestId: facts.id,
            body: { kind: kind as "STAFF_NOTE", body: text },
          }),
        };
      }}
    >
      <SelectField
        id="request-note-kind"
        label="Kind of note"
        value={kind}
        onChange={setKind}
        options={noteKindOptions}
        required
      />
      <TextAreaField
        id="request-note-body"
        label="Note"
        hint="Staff only. Never shown to the customer."
        value={body}
        rows={5}
        maxLength={NOTE_MAX}
        required
        error={error}
        onChange={setBody}
      />
    </RequestCommandDialog>
  );
}
